import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { rackWidthM } from '@eombor/contracts';
import { Maximize2, Minus, Plus, Scan } from 'lucide-react';
import { Button, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { isSelected, type BuilderSelection, type DraftRack, type DraftZone } from '../model/builder.store.js';
import {
  GRID_STEP,
  rackWithinZone,
  snap,
  type PlanRect,
  type WarehouseFloor,
} from '../model/plan-geometry.js';
import {
  clampView,
  contentBounds,
  fitView,
  zoomAt,
  MAX_ZOOM,
  MIN_ZOOM,
  type PlanView,
} from '../model/plan-view.js';

/** Chapdagi vositalar paneli tanlagan rejim. */
export type BuilderTool = 'select' | 'zone' | 'rack' | 'delete';

export interface PlanCanvasProps {
  floor: WarehouseFloor;
  zones: DraftZone[];
  racks: DraftRack[];
  selected: BuilderSelection | null;
  tool: BuilderTool;
  onSelect: (selected: BuilderSelection | null) => void;
  onMoveRack: (id: string, x: number, y: number) => void;
  onRequestDelete: (target: BuilderSelection) => void;
  onDrawZone: (rect: PlanRect) => void;
  /** Zonani sudrash — javon kabi, panjaraga 0,25 m qadam bilan yopishadi. */
  onMoveZone: (id: string, x: number, y: number) => void;
  onAddRackAt: (x: number, y: number) => void;
  className?: string;
}

/**
 * Chizmadagi chekka (metrda). Kontur kesilib qolmasligi uchun emas, aynan
 * O'LCHAM CHIZIQLARI uchun: ular ombor konturidan tashqarida turadi, xuddi
 * qog'ozdagi chizmada bo'lgani kabi.
 */
const PAD = 2.6;

/** Chizma matnining o'lchami, metrda — 60 m li rejada ~14 px ga to'g'ri keladi. */
const TEXT = 0.9;

/**
 * MASSHTAB — nega kerak.
 *
 * Reja butun polni bitta ekranga sig'dirib chizardi. 80 × 80 m ombor uchun
 * bu shuni bildiradi: 8 × 4 m zona ekranda tirnoq kattaligida, 1,1 m
 * chuqurlikdagi javon esa chiziqqa aylanadi. Bunday ko'rinishda javonni
 * sichqoncha bilan joylashtirish amalda mumkin emas — 25 sm panjara qadami
 * bir necha pikselga to'g'ri keladi.
 *
 * Shuning uchun `viewBox` endi qo'zg'aluvchan: `zoom` — polning ekranga
 * nisbati, `x`/`y` — ko'rinayotgan burchak. Chiziq va matn o'lchamlari
 * `1 / zoom` ga ko'paytiriladi, ya'ni ular EKRANDA doimiy qoladi: yaqin
 * kelganda yozuv kattalashib ketmaydi, uzoqlashganda yo'qolmaydi.
 */
/** Bosish va sudrashni ajratadigan masofa, pikselda. */
const DRAG_THRESHOLD_PX = 4;

interface DimensionProps {
  from: number;
  to: number;
  /** Gorizontalda — `y`, vertikalda — `x`. */
  at: number;
  vertical?: boolean;
  label: string;
  color?: string;
  /** Masshtab ko'paytuvchisi (`1 / zoom`) — chiziq va raqam ekranda doimiy qolsin. */
  k: number;
}

/**
 * O'LCHAM CHIZIG'I — qog'ozdagi chizmadagidek: ikki chetida tikka chiziqcha,
 * o'rtasida raqam.
 *
 * Rejaning asosiy kamchiligi shu edi: yashil to'rtburchak "qanchalik katta"
 * degan savolga umuman javob bermasdi va odam uni ko'z bilan chamalashga
 * majbur bo'lardi. Raqam esa chamalashni butunlay ortiqcha qiladi.
 */
const Dimension = ({ from, to, at, vertical = false, label, color, k }: DimensionProps) => {
  const tick = 0.35 * k;
  const middle = (from + to) / 2;
  const stroke = { stroke: color ?? 'currentColor', strokeWidth: 0.05 * k };

  return (
    <g className="fill-text-faint text-text-faint" style={{ pointerEvents: 'none' }}>
      {vertical ? (
        <>
          <line x1={at} y1={from} x2={at} y2={to} {...stroke} />
          <line x1={at - tick} y1={from} x2={at + tick} y2={from} {...stroke} />
          <line x1={at - tick} y1={to} x2={at + tick} y2={to} {...stroke} />
          {/* Vertikal o'lcham raqami chizma bo'ylab buriladi — chizmachilik odati. */}
          <text
            x={at - 0.35 * k}
            y={middle}
            fontSize={TEXT * k}
            textAnchor="middle"
            transform={`rotate(-90 ${at - 0.35 * k} ${middle})`}
          >
            {label}
          </text>
        </>
      ) : (
        <>
          <line x1={from} y1={at} x2={to} y2={at} {...stroke} />
          <line x1={from} y1={at - tick} x2={from} y2={at + tick} {...stroke} />
          <line x1={to} y1={at - tick} x2={to} y2={at + tick} {...stroke} />
          <text x={middle} y={at - 0.35 * k} fontSize={TEXT * k} textAnchor="middle">
            {label}
          </text>
        </>
      )}
    </g>
  );
};

/** Reja METRDA o'qiladi — santimetr bu masshtabda ma'nosiz uzun bo'lardi. */
const metres = (value: number): string =>
  `${Number.isInteger(value) ? value : value.toFixed(1).replace('.', ',')} m`;

interface DragState {
  kind: 'move' | 'zone' | 'zone-move';
  id: string;
  /** Sichqoncha bilan markaz orasidagi masofa (metrda) — javon "sakramaydi". */
  offsetX: number;
  offsetY: number;
  startX: number;
  startY: number;
}

/**
 * Omborning tepadan ko'rinishi — konstruktorning asosiy ish maydoni.
 *
 * SVG ataylab tanlangan: reja bosib chiqarilishi kerak (ombor devorida
 * osilgan A3 varaq), SVG esa har qanday o'lchamda aniq chiqadi. Sudrash
 * `dnd-kit` bilan emas, xom `pointer` hodisalari bilan — bizga elementlar
 * ro'yxatidagi tartib emas, **metrdagi koordinata** kerak, va u panjaraga
 * 0,25 m qadam bilan yopishadi.
 *
 * O'q tartibi: X o'ngga, Y **pastga** — qog'ozdagi reja shunday chiziladi
 * (koordinata boshi chap yuqori burchakda).
 */
export const PlanCanvas = ({
  floor,
  zones,
  racks,
  selected,
  tool,
  onSelect,
  onMoveRack,
  onRequestDelete,
  onDrawZone,
  onMoveZone,
  onAddRackAt,
  className,
}: PlanCanvasProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [preview, setPreview] = useState<PlanRect | null>(null);

  /** Ekran koordinatasini ombor metriga aylantiradi (SVG matritsasi orqali). */
  const toMeters = useCallback((event: PointerEvent): { x: number; y: number } | null => {
    const svg = svgRef.current;
    if (svg === null) return null;
    const ctm = svg.getScreenCTM();
    if (ctm === null) return null;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const local = point.matrixTransform(ctm.inverse());
    return { x: local.x - PAD, y: local.y - PAD };
  }, []);

  /* Butun pol chizmaga sig'adigan boshlang'ich o'lcham (metrda). */
  const base = useMemo(
    () => ({ w: floor.lengthM + PAD * 2, h: floor.widthM + PAD * 2 }),
    [floor.lengthM, floor.widthM],
  );

  const [view, setView] = useState<PlanView>({ x: 0, y: 0, zoom: 1 });

  /* Ombor o'lchami o'zgarsa (masalan tahrirlashdan keyin) ko'rinish
     boshiga qaytariladi: eski `x`/`y` yangi polda ma'nosiz bo'lardi. */
  useEffect(() => {
    setView({ x: 0, y: 0, zoom: 1 });
  }, [base.w, base.h]);

  const size = useMemo(
    () => ({ w: base.w / view.zoom, h: base.h / view.zoom }),
    [base, view.zoom],
  );

  /** Ekranda doimiy qolishi kerak bo'lgan o'lchamlar uchun ko'paytuvchi. */
  const k = 1 / view.zoom;

  const clamp = useCallback((next: PlanView): PlanView => clampView(next, base), [base]);

  /** Berilgan to'rtburchakni (metrda, pol koordinatasida) kadrga oladi. */
  const focusRect = useCallback(
    (rect: PlanRect): void => setView(fitView(rect, base, PAD)),
    [base],
  );

  const contentRect = useMemo(() => contentBounds(zones, racks), [zones, racks]);

  const gridLines = useMemo(() => {
    const vertical: number[] = [];
    const horizontal: number[] = [];
    for (let x = 0; x <= Math.floor(floor.lengthM); x += 1) vertical.push(x);
    for (let y = 0; y <= Math.floor(floor.widthM); y += 1) horizontal.push(y);
    return { vertical, horizontal };
  }, [floor]);

  /*
   * G'ildirak NATIV tinglovchi orqali: React `wheel` ni passiv qilib
   * qo'shadi va `preventDefault()` ishlamaydi — sahifa reja bilan birga
   * pastga sirg'alib ketardi.
   *
   * Yaqinlashish KURSOR ostidagi nuqta atrofida bo'ladi: ekranda barmoq
   * qo'ygan joy o'z o'rnida qoladi. Markazga nisbatan yaqinlashish
   * qaralayotgan javonni kadrdan chiqarib yuborardi.
   */
  useEffect(() => {
    const svg = svgRef.current;
    if (svg === null) return;

    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const ctm = svg.getScreenCTM();
      if (ctm === null) return;
      const point = svg.createSVGPoint();
      point.x = event.clientX;
      point.y = event.clientY;
      const local = point.matrixTransform(ctm.inverse());

      setView((current) =>
        zoomAt(current, base, { x: local.x, y: local.y }, Math.exp(-event.deltaY * 0.0015)),
      );
    };

    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [base]);

  const finishDrag = (event: PointerEvent): void => {
    const pan = panRef.current;
    if (pan !== null) {
      panRef.current = null;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      // Surilmagan bosish — bo'sh joyga bosish, ya'ni tanlovni bekor qilish.
      if (!pan.moved) onSelect(null);
      return;
    }

    const drag = dragRef.current;
    if (drag !== null && drag.kind === 'zone' && preview !== null) {
      if (preview.w >= GRID_STEP && preview.d >= GRID_STEP) onDrawZone(preview);
    }

    /*
     * ZONANI JOYIDA BOSISH — uni kadrga oladi.
     *
     * Katta ombordagi kichik zonaga yetib borishning eng tez yo'li: aks
     * holda g'ildirak bilan yaqinlashib, keyin zonani qidirib surishga
     * to'g'ri keladi. Uzoqlashtirish esa butun maydonni qaytarib beradi.
     *
     * SUDRALGAN bosish bunga kirmaydi: operator zonani ko'chirgan bo'lsa,
     * ko'rinishning sakrashi uni yo'qotib qo'yardi. Shuning uchun
     * boshlang'ich va joriy koordinata solishtiriladi.
     */
    if (drag !== null && drag.kind === 'zone-move') {
      const zone = zones.find((candidate) => candidate.id === drag.id);
      const stayed =
        zone !== undefined && zone.plan.x === drag.startX && zone.plan.y === drag.startY;
      if (stayed) focusRect(zone.plan);
    }
    dragRef.current = null;
    setDragging(null);
    setPreview(null);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  /*
   * BO'SH JOYNI SUDRASH — surilish.
   *
   * Modifikator tugmasiz: reja katta bo'lganda surilish eng ko'p
   * ishlatiladigan amal va uni `Alt` orqasiga yashirish uni topilmas
   * qilardi. Bosish bilan sudrash `DRAG_THRESHOLD_PX` orqali ajratiladi:
   * joyida bosish — tanlovni bekor qiladi, surilgan bosish — rejani
   * ko'chiradi.
   */
  const panRef = useRef<{
    clientX: number;
    clientY: number;
    viewX: number;
    viewY: number;
    moved: boolean;
  } | null>(null);

  const handleBackgroundPointerDown = (event: PointerEvent<SVGSVGElement>): void => {
    const point = toMeters(event);
    if (point === null) return;

    if (tool === 'select') {
      panRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
        viewX: view.x,
        viewY: view.y,
        moved: false,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    if (tool === 'rack') {
      onAddRackAt(snap(point.x), snap(point.y));
      return;
    }
    if (tool === 'zone') {
      dragRef.current = {
        kind: 'zone',
        id: 'new',
        offsetX: 0,
        offsetY: 0,
        startX: snap(point.x),
        startY: snap(point.y),
      };
      setDragging('new');
      setPreview({ x: snap(point.x), y: snap(point.y), w: 0, d: 0 });
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }
    onSelect(null);
  };

  const handlePointerMove = (event: PointerEvent<SVGSVGElement>): void => {
    const pan = panRef.current;
    if (pan !== null) {
      const dx = event.clientX - pan.clientX;
      const dy = event.clientY - pan.clientY;
      if (!pan.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      pan.moved = true;

      /* Ekran pikselini metrga aylantirish: `viewBox` ning kengligi
         elementning piksel kengligiga to'g'ri keladi. */
      const rect = event.currentTarget.getBoundingClientRect();
      if (rect.width === 0) return;
      const perPixel = size.w / rect.width;
      setView(clamp({ zoom: view.zoom, x: pan.viewX - dx * perPixel, y: pan.viewY - dy * perPixel }));
      return;
    }

    const drag = dragRef.current;
    if (drag === null) return;
    const point = toMeters(event);
    if (point === null) return;

    if (drag.kind === 'zone') {
      const x = Math.min(drag.startX, snap(point.x));
      const y = Math.min(drag.startY, snap(point.y));
      setPreview({
        x,
        y,
        w: Math.abs(snap(point.x) - drag.startX),
        d: Math.abs(snap(point.y) - drag.startY),
      });
      return;
    }

    if (drag.kind === 'zone-move') {
      onMoveZone(drag.id, point.x - drag.offsetX, point.y - drag.offsetY);
      return;
    }

    if (!racks.some((candidate) => candidate.id === drag.id)) return;

    /* Rejada FAQAT KO'CHIRISH bor. O'lcham sudrash ham bo'lgan edi va u
       jimgina proyomlarni qayta o'lchardi: oynada "8 proyom × 150 sm"
       yozilgan javon bir tortishda 2 m bo'lib qolardi. O'lcham endi
       yagona joyda — stellaj oynasida. */
    if (drag.kind === 'move') {
      onMoveRack(drag.id, point.x - drag.offsetX, point.y - drag.offsetY);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<SVGSVGElement>): void => {
    // Strelkalar faqat STELLAJNI ko'chiradi. Tanlov turi tekshirilmasa,
    // zona bosilganida o'sha raqamli stellaj sudralib ketardi.
    if (selected === null || selected.kind !== 'rack') return;
    const rack = racks.find((candidate) => candidate.id === selected.id);
    if (rack === undefined) return;

    const step = event.shiftKey ? GRID_STEP * 4 : GRID_STEP;
    switch (event.key) {
      case 'ArrowLeft':
        onMoveRack(rack.id, rack.pos.x - step, rack.pos.y);
        break;
      case 'ArrowRight':
        onMoveRack(rack.id, rack.pos.x + step, rack.pos.y);
        break;
      case 'ArrowUp':
        onMoveRack(rack.id, rack.pos.x, rack.pos.y - step);
        break;
      case 'ArrowDown':
        onMoveRack(rack.id, rack.pos.x, rack.pos.y + step);
        break;
      case 'Delete':
      case 'Backspace':
        onRequestDelete({ kind: 'rack', id: rack.id });
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  const zoneById = useMemo(() => new Map(zones.map((zone) => [zone.id, zone])), [zones]);

  const zoomBy = (factor: number): void =>
    setView((current) => clamp({ ...current, zoom: current.zoom * factor }));

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* MASSHTAB BOSHQARUVI. "Mazmunga sig'dirish" birinchi turadi: katta
          omborda eng ko'p kerak bo'ladigan amal aynan shu — zonalar polning
          kichik bir qismini egallaganda ularni qo'lda topib yurish o'rniga
          bir bosishda kadrga olinadi. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={contentRect === null}
          onClick={() => {
            if (contentRect !== null) focusRect(contentRect);
          }}
        >
          <Scan size={14} aria-hidden />
          {t('builder.fitContent')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setView({ x: 0, y: 0, zoom: 1 })}
        >
          <Maximize2 size={14} aria-hidden />
          {t('builder.fitFloor')}
        </Button>

        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('builder.zoomOut')}
            disabled={view.zoom <= MIN_ZOOM}
            onClick={() => zoomBy(1 / 1.4)}
          >
            <Minus size={14} aria-hidden />
          </Button>
          <span className="tabular text-text-muted w-[3.5rem] text-center text-xs">
            {`${Math.round(view.zoom * 100)}%`}
          </span>
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('builder.zoomIn')}
            disabled={view.zoom >= MAX_ZOOM}
            onClick={() => zoomBy(1.4)}
          >
            <Plus size={14} aria-hidden />
          </Button>
        </div>
      </div>

      <svg
        ref={svgRef}
        role="application"
        aria-label={t('builder.planLabel')}
        tabIndex={0}
        viewBox={`${view.x} ${view.y} ${size.w} ${size.h}`}
        onPointerDown={handleBackgroundPointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full rounded-[var(--radius-sm)] bg-surface-2',
          /* MATN TANLANMAYDI. Reja sudrab suriladi va sudrash yo'l-yo'lakay
             javon kodini "belgilab" qo'yardi: javonning ustida qora
             to'rtburchak paydo bo'lib, chizma buzilgandek ko'rinardi. */
          'select-none',
          'outline-none focus-visible:shadow-[var(--shadow-focus)]',
          tool === 'zone' || tool === 'rack' ? 'cursor-crosshair' : 'cursor-grab',
          'touch-none',
        )}
      >
        <g transform={`translate(${PAD} ${PAD})`}>
          {/* 1 m panjara — o'lchamni ko'z bilan chamalash uchun. */}
          <g className="stroke-border opacity-[0.15]" strokeWidth={0.02 * k}>
            {gridLines.vertical.map((x) => (
              <line key={`v${x}`} x1={x} y1={0} x2={x} y2={floor.widthM} />
            ))}
            {gridLines.horizontal.map((y) => (
              <line key={`h${y}`} x1={0} y1={y} x2={floor.lengthM} y2={y} />
            ))}
          </g>

          {/* Ombor konturi */}
          <rect
            x={0}
            y={0}
            width={floor.lengthM}
            height={floor.widthM}
            fill="none"
            strokeWidth={0.12 * k}
            className="stroke-border-strong"
          />

          {/* Ombor gabariti — har doim ko'rinadi: rejaga qaragan odam
              birinchi navbatda "bu qanchalik katta?" deb so'raydi. */}
          <Dimension k={k} from={0} to={floor.lengthM} at={-0.9 * k} label={metres(floor.lengthM)} />
          <Dimension k={k} vertical from={0} to={floor.widthM} at={-0.9 * k} label={metres(floor.widthM)} />

          {zones.map((zone) => {
            const isActive = isSelected(selected, 'zone', zone.id);
            return (
              <g key={zone.id}>
                <rect
                  x={zone.plan.x}
                  y={zone.plan.y}
                  width={zone.plan.w}
                  height={zone.plan.d}
                  fill={zone.color}
                  fillOpacity={0.12}
                  stroke={zone.color}
                  strokeOpacity={isActive ? 0.95 : 0.5}
                  strokeWidth={(isActive ? 0.14 : 0.08) * k}
                  style={{ cursor: tool === 'select' ? 'grab' : undefined }}
                  onPointerDown={(event) => {
                    if (tool !== 'select' && tool !== 'delete') return;
                    event.stopPropagation();
                    if (tool === 'delete') {
                      onRequestDelete({ kind: 'zone', id: zone.id });
                      return;
                    }
                    onSelect({ kind: 'zone', id: zone.id });
                    const point = toMeters(event);
                    if (point === null) return;
                    /* Ofset — sichqoncha bilan zonaning BURCHAGI orasidagi
                       masofa: aks holda zona bosilgan zahoti sakrab, kursor
                       o'z burchagiga tortib olardi. */
                    dragRef.current = {
                      kind: 'zone-move',
                      id: zone.id,
                      offsetX: point.x - zone.plan.x,
                      offsetY: point.y - zone.plan.y,
                      startX: zone.plan.x,
                      startY: zone.plan.y,
                    };
                    setDragging(zone.id);
                    svgRef.current?.setPointerCapture(event.pointerId);
                  }}
                />
              </g>
            );
          })}

          {/* ZONA YOZUVLARI javonlardan KEYIN chiziladi: SVG da chizish
              tartibi ustma-ustlikni belgilaydi va javon yozuvni bosib
              qolayotgan edi. */}
          {zones.map((zone) => {
            const isActive = isSelected(selected, 'zone', zone.id);
            return (
              <g key={`label-${zone.id}`} style={{ pointerEvents: 'none' }}>
                <text
                  x={zone.plan.x + 0.4}
                  y={zone.plan.y + 1.2}
                  fontSize={TEXT * k}
                  className="fill-text-muted"
                  style={{ pointerEvents: 'none' }}
                >
                  {`${zone.code} — ${zone.name}`}
                </text>
                {/* O'lchami nomning ostida — zona tanlanmagan paytda ham
                    ko'rinib turadi, chunki savol doim shu. */}
                <text
                  x={zone.plan.x + 0.4}
                  y={zone.plan.y + 2.3}
                  fontSize={TEXT * 0.85 * k}
                  fill={zone.color}
                  style={{ pointerEvents: 'none' }}
                >
                  {`${metres(zone.plan.w)} × ${metres(zone.plan.d)}`}
                </text>

                {/* Chizma o'lchamlari faqat TANLANGAN zonada: hamma zonada
                    bir vaqtda chizilsa reja raqamlar to'riga aylanib qolardi. */}
                {isActive ? (
                  <>
                    <Dimension
                      k={k}
                      from={zone.plan.x}
                      to={zone.plan.x + zone.plan.w}
                      at={zone.plan.y - 0.7}
                      label={metres(zone.plan.w)}
                      color={zone.color}
                    />
                    <Dimension
                      k={k}
                      vertical
                      from={zone.plan.y}
                      to={zone.plan.y + zone.plan.d}
                      at={zone.plan.x - 0.7}
                      label={metres(zone.plan.d)}
                      color={zone.color}
                    />
                  </>
                ) : null}
              </g>
            );
          })}

          {racks.map((rack) => {
            const isActive = isSelected(selected, 'rack', rack.id);
            const halfW = rackWidthM(rack) / 2;
            const halfD = rack.depthM / 2;
            /* Javon O'Z ZONASINING rangida chiziladi, bir xil kulrangda
               emas: rejada o'nlab javon bo'ladi va qaysi biri qaysi zonaga
               tegishli ekani bir qarashda ko'rinishi kerak. */
            const zone = zoneById.get(rack.zoneId);
            const tint = zone?.color ?? 'currentColor';
            /* Zonasidan chiqib ketgan javon BELGILANADI, lekin ko'chirilmaydi:
               u allaqachon shunday qurilgan bo'lishi mumkin va uni jimgina
               surib qo'yish etiketkalar bilan haqiqiy javon orasidagi
               bog'lanishni buzardi. */
            const escaped = zone !== undefined && !rackWithinZone(rack, zone.plan);
            /* Ichki chegaralar: birinchi va oxirgisi javonning o'z cheti.
               Rejada javon TEPADAN ko'rinadi, ya'ni bitta qatorni tanlash
               kerak — pastkisi olinadi, chunki u polda turadi va odam
               javonga kirganda birinchi o'shani ko'radi. */
            const row = rack.bays[0] ?? [];
            const bayEdges = row
              .slice(0, -1)
              .map((_, index) => -halfW + row.slice(0, index + 1).reduce((a, b) => a + b, 0));

            return (
              <g
                key={rack.id}
                transform={`translate(${rack.pos.x} ${rack.pos.y}) rotate(${rack.pos.rotationDeg})`}
              >
                <rect
                  x={-halfW}
                  y={-halfD}
                  width={halfW * 2}
                  height={halfD * 2}
                  rx={0.08}
                  fill={tint}
                  fillOpacity={0.85}
                  stroke={tint}
                  className={cn(
                    isActive ? 'stroke-accent' : escaped ? 'stroke-warn' : undefined,
                    dragging === rack.id ? 'drop-shadow-[var(--shadow-2)]' : undefined,
                  )}
                  strokeDasharray={escaped && !isActive ? `${0.4 * k} ${0.25 * k}` : undefined}
                  strokeWidth={(isActive || escaped ? 0.14 : 0.06) * k}
                  /* Har javonning NOMI bor. Ilgari u faqat zonadan chiqib
                     ketganda paydo bo'lardi, ya'ni rejadagi o'nlab
                     to'rtburchak ekran o'quvchisi uchun bir xil, nomsiz
                     shakl edi. */
                  aria-label={
                    escaped
                      ? t('builder.rackOutsideZone', { code: rack.code })
                      : t('builder.rackLabel', { code: rack.code })
                  }
                  style={{ cursor: tool === 'select' ? 'grab' : undefined }}
                  onPointerDown={(event) => {
                    event.stopPropagation();
                    if (tool === 'delete') {
                      onRequestDelete({ kind: 'rack', id: rack.id });
                      return;
                    }
                    if (tool !== 'select') return;
                    onSelect({ kind: 'rack', id: rack.id });
                    const point = toMeters(event);
                    if (point === null) return;
                    dragRef.current = {
                      kind: 'move',
                      id: rack.id,
                      offsetX: point.x - rack.pos.x,
                      offsetY: point.y - rack.pos.y,
                      startX: rack.pos.x,
                      startY: rack.pos.y,
                    };
                    setDragging(rack.id);
                    svgRef.current?.setPointerCapture(event.pointerId);
                  }}
                />

                {/* Proyom chegaralari — ramalar shu joyda turadi. Reja
                    stellajning ICHKI bo'linishini ham ko'rsatishi kerak, aks
                    holda "umumiy uzunlik" bilan "qismlar" bog'lanmay qoladi. */}
                {bayEdges.map((offset) => (
                  <line
                    key={offset}
                    x1={offset}
                    y1={-halfD}
                    x2={offset}
                    y2={halfD}
                    className="stroke-surface-1"
                    strokeWidth={0.05 * k}
                    pointerEvents="none"
                  />
                ))}

                {/* Javon kodi javonning O'ZIDA — reja bosib chiqarilganda
                    ham qaysi javon ekani ko'rinib tursin. */}
                <text
                  x={0}
                  y={0.22}
                  fontSize={Math.min(0.62, halfD * 0.9)}
                  textAnchor="middle"
                  fill="var(--color-surface)"
                  style={{ pointerEvents: 'none' }}
                >
                  {rack.code}
                </text>

                {isActive ? (
                  <Dimension
                    k={k}
                    from={-halfW}
                    to={halfW}
                    at={-halfD - 0.6}
                    label={metres(halfW * 2)}
                    color={tint}
                  />
                ) : null}

              </g>
            );
          })}

          {preview === null ? null : (
            <rect
              x={preview.x}
              y={preview.y}
              width={preview.w}
              height={preview.d}
              className="fill-accent stroke-accent"
              fillOpacity={0.12}
              strokeWidth={0.1 * k}
              strokeDasharray={`${0.3 * k} ${0.2 * k}`}
            />
          )}
        </g>
      </svg>

      <p className="text-xs text-text-faint">{t('builder.planHint')}</p>
    </div>
  );
};
